import { useMemo, useState } from 'react';

import { WidgetViewMode, WidgetViewModeUtils } from 'utilitys/reporting/widgetViewModeUtils';
import { useShallow } from 'zustand/react/shallow';

import { useGlobalFiltersHook } from 'hooks/useGlobalFiltersHook';

import { useGlobalFilterDataStore } from 'store/globalFilterData.store';
import { useValidationStore } from 'store/validation.store';

import { useQueryGetLogicCheckerCategoryResults } from 'api/queries/getLogicCheckerCategoryResults.query';
import { useQueryGetLogicCheckerSummary } from 'api/queries/getLogicCheckerSummary.query';

import { FilterNames, getFiltersData } from 'components/common/GlobalFiltersInPage/globalFiltersInPage.utils';

import { useResolvedParams } from 'components/widgets/LogicQualityCategoriesTable/hooks/useResolvedParams';

export type UseLogicQualityCategoriesTableProvider = ReturnType<typeof useLogicQualityCategoriesTableProvider>;

export const useLogicQualityCategoriesTableProvider = ({
    localFilters,
    widgetViewMode,
    projectId,
    contractId,
    latestVersionId,
    componentKey,
    globalFilterData,
    widgetId,
    endpoints,
}) => {
    //store values
    const {
        isDcma: isDcmaFromStore,
        isActive: isActiveFromStore,
        isFailedChecksOnly: isFailedChecksOnlyFromStore,
        update,
    } = useValidationStore(
        useShallow((state) => ({
            isDcma: state.isDcma,
            isActive: state.isActive,
            update: state.update,
            isFailedChecksOnly: state.isFailedChecksOnly,
        })),
    );

    //local states with initials from store or from report and setters
    const [isDcma, setIsDcma] = useState<boolean>(resolveInitialIsDcma(widgetViewMode, localFilters, isDcmaFromStore));
    const [isActive, setIsActive] = useState<boolean>(
        resolveInitialIsActive(widgetViewMode, localFilters, isActiveFromStore),
    );
    const [isFailedChecksOnly, setIsFailedChecksOnly] = useState<boolean>(
        resolveInitialIsFailedChecksOnly(widgetViewMode, localFilters, isFailedChecksOnlyFromStore),
    );
    const onSetIsDcma = (value: boolean) => {
        if (WidgetViewModeUtils.isPageView(widgetViewMode)) {
            update({ isDcma: value });
        }
        setIsDcma(value);
    };
    const onSetIsActive = (value: boolean) => {
        if (WidgetViewModeUtils.isPageView(widgetViewMode)) {
            update({ isActive: value });
        }
        setIsActive(value);
    };
    const onSetIsFailedChecksOnly = (value: boolean) => {
        if (WidgetViewModeUtils.isPageView(widgetViewMode)) {
            update({ isFailedChecksOnly: value });
        }
        setIsFailedChecksOnly(value);
    };

    // values resolver from url params or props
    const {
        versionName,
        versionDate,
        resolvedCategory,
        selectedVersionId,
        resolvedProjectId,
        resolvedContractId,
        latestVersion,
    } = useResolvedParams({ projectId, contractId, latestVersionId, componentKey });

    const globalFilterDataFromStore = useGlobalFilterDataStore((state) => state.globalFilterData);

    // source
    const source = WidgetViewModeUtils.isReportView(widgetViewMode)
        ? localFilters
        : WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode)
          ? { ...globalFilterData, isDcma, isActive, isFailedChecksOnly }
          : { ...globalFilterDataFromStore, isDcma, isActive, isFailedChecksOnly };

    const filters = useMemo(
        () =>
            getFiltersData({
                [FilterNames.ACTIVITY_CODES]: source?.activityCodes
                    ?.filter((item) => !item.isExclude)
                    .map((i) => i.name),
                [FilterNames.ACTIVITY_CODES_EXCLUDE]: source?.activityCodes
                    ?.filter((item) => item.isExclude)
                    .map((i) => i.name),
                [FilterNames.FRAGNET_SEARCH]: source?.milestoneFragnet,
                [FilterNames.TAG_CARD_BOARD]: source?.tags?.filter((item) => !item.isExclude).map((i) => i.name),
                [FilterNames.TAG_CARD_BOARD_EXCLUDE]: source?.tags?.filter((item) => item.isExclude).map((i) => i.name),
                [FilterNames.WBS_SEARCH]: source?.wbs,
            }),

        [source?.activityCodes, source?.milestoneFragnet, source?.tags, source?.wbs],
    );

    //Pills
    const pillsData = {
        milestoneFragnet: source?.milestoneFragnet,
        tags: source?.tags?.filter((item) => !item.isExclude).map((i) => i.name),
        tagsExclude: source?.tags?.filter((item) => item.isExclude).map((i) => i.name),
        activityCodes: source?.activityCodes?.filter((item) => !item.isExclude).map((i) => i.name),
        activityCodesExclude: source?.activityCodes?.filter((item) => item.isExclude).map((i) => i.name),
        wbs: source?.wbs,
        ...(source?.isDcma && { 'DCMA Only': 'Yes' }),
        ...(source?.isActive && { 'Active Checks Only': 'Yes' }),
        ...(source?.isFailedChecksOnly && { 'Failed Checks Only': 'Yes' }),
    };
    const { pillsList: pills } = useGlobalFiltersHook({ source: pillsData, latestVersionId });

    const getLogicCheckerCategoryResultsPayload = {
        fileMetaDataId: selectedVersionId,
        filter: {
            filters,
        },
    };
    return {
        ui: {
            state: {
                //global filters
                tags: source?.tags,
                wbs: source?.wbs,
                activityCodes: source?.activityCodes,
                milestoneFragnet: source?.milestoneFragnet,
                // local state
                isDcma: source?.isDcma,
                isActive: source?.isActive,
                isFailedChecksOnly: source?.isFailedChecksOnly,
            },
            pills,
            vars: {
                resolvedProjectId,
                resolvedContractId,
                resolvedCategory,
                selectedVersionId,
                versionName,
                versionDate,
                latestVersion,
            },
            setters: {
                onSetIsDcma,
                onSetIsActive,
                onSetIsFailedChecksOnly,
            },
        },
        api: {
            logicCheckerCategoryResults: {
                instance: useQueryGetLogicCheckerCategoryResults(
                    getLogicCheckerCategoryResultsPayload,
                    endpoints.getLogicCheckerCategoryResults,
                ),
                payload: getLogicCheckerCategoryResultsPayload,
                endpoint: endpoints.getLogicCheckerCategoryResults,
            },
            logicCheckerSummary: useQueryGetLogicCheckerSummary({
                fileMetaDataId: selectedVersionId,
                filter: {
                    filters,
                },
            }),
        },
    };
};

//region [[ Utils ]]
const resolveInitialIsFailedChecksOnly = (
    widgetViewMode: WidgetViewMode,
    localFilters: any,
    isFailedChecksOnlyFromStore: boolean,
) => {
    // report edit OR report view → from local or defaults to false
    if (WidgetViewModeUtils.isReportEditWidget(widgetViewMode) || WidgetViewModeUtils.isReportView(widgetViewMode)) {
        return localFilters?.isFailedChecksOnly ?? false;
    }

    // page → from store
    if (WidgetViewModeUtils.isPageView(widgetViewMode)) {
        return isFailedChecksOnlyFromStore;
    }

    // report new (or any fallback) → true
    return false;
};

const resolveInitialIsDcma = (widgetViewMode: WidgetViewMode, localFilters: any, isDcmaFromStore: boolean) => {
    // report edit OR report view → from local or defaults to false
    if (WidgetViewModeUtils.isReportEditWidget(widgetViewMode) || WidgetViewModeUtils.isReportView(widgetViewMode)) {
        return localFilters?.isDcma ?? false;
    }

    // page → from store
    if (WidgetViewModeUtils.isPageView(widgetViewMode)) {
        return isDcmaFromStore;
    }

    // report new (or any fallback) → true
    return false;
};

const resolveInitialIsActive = (widgetViewMode: WidgetViewMode, localFilters: any, isActiveFromStore: boolean) => {
    // report edit OR report view → from local or defaults to true
    if (WidgetViewModeUtils.isReportEditWidget(widgetViewMode) || WidgetViewModeUtils.isReportView(widgetViewMode)) {
        return localFilters?.isActive ?? true;
    }

    // page → from store
    if (WidgetViewModeUtils.isPageView(widgetViewMode)) {
        return isActiveFromStore;
    }

    // report new (or any fallback) → true
    return true;
};
//endregion [[ Utils ]]
